import { z } from "zod";

import { generateBlankError } from ".";
import { createListBoxSchema } from "./common";

const shapeSchema = z
  .string()
  .trim()
  .refine(
    (value) => {
      const regex = /^-?\d+(,-?\d+)*$/;
      return regex.test(value);
    },
    {
      message: "Should contain only comma-delimited integers",
    },
  );

const batchSizeSchema = z
  .string()
  .trim()
  .refine((value) => /^-?\d+$/.test(value), {
    message: "Value should be an integer (no decimals)",
  });

const innerSchema = z.object({
  projectName: z.string().trim().min(1, "Name is required"),
  currentCost: z.string().trim().min(1, "Cost is required"),
  useCase: z.string().trim().min(1, "Use case is required"),
  currentRevenue: createListBoxSchema<string>(),
  currentDeployment: createListBoxSchema<string>(),
  currentDeploymentOthers: z.custom<string>(),
  productInfo: createListBoxSchema<string>()
    .array()
    .min(1, "Please select at least one"),
  modelType: z.enum(["pytorch", "onnx"]),
  modelFiles: z.custom<File>().array().min(1, "Please upload a model"),
  batchSize: batchSizeSchema,
  inputShape: shapeSchema,
  outputShape: shapeSchema,
  inputDType: createListBoxSchema<string>(),
  outputDType: createListBoxSchema<string>(),
  sampleDataFiles: z
    .custom<File>()
    .array()
    .min(1, "Please upload your sample data"),
});

export const newProjectSchema = innerSchema
  .superRefine((data, ctx) => {
    if (
      data.currentDeployment?.value === "Others" &&
      data.currentDeploymentOthers?.length <= 2
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please specify",
        path: ["currentDeploymentOthers"],
      });
    }
  })
  // Verify ONNX model
  .superRefine((data, ctx) => {
    if (data.modelType !== "onnx") {
      return;
    }
    if (
      data.modelFiles.length != 1 ||
      !data.modelFiles[0].name.endsWith(".onnx")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Only .onnx file is expected",
        path: ["modelFiles"],
      });
    }
  })
  // Verify Pytorch model
  .superRefine((data, ctx) => {
    if (data.modelType !== "pytorch") {
      return;
    }
    if (
      data.modelFiles.length != 1 ||
      !data.modelFiles[0].name.endsWith(".pt")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Only .pt file is expected",
        path: ["modelFiles"],
      });
    }
  })
  // Verify uploaded sample data
  .superRefine((data, ctx) => {
    if (data.sampleDataFiles.length < 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Insufficient sample data",
        path: ["sampleDataFiles"],
      });
      return;
    }

    let count = 0;
    let haveIn = false;
    for (const file of data.sampleDataFiles) {
      const expectedFileName = `${count}_${!haveIn ? "in" : "out"}.npy`;
      if (file.name !== expectedFileName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Unexpected file: ${file.name}, expected: ${expectedFileName}`,
          path: ["sampleDataFiles"],
        });
        return;
      }

      if (haveIn) count += 1;
      haveIn = !haveIn;
    }

    // provided input file without output
    if (haveIn) {
      const expectedFileName = `${count}_out.npy`;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Missing file: ${expectedFileName}`,
        path: ["sampleDataFiles"],
      });
    }
  });

const schemaKeys = innerSchema.keyof();

export type NewProjectSchemaError = Record<
  keyof typeof innerSchema.shape,
  string
>;

export const newProjectSchemaBlankError: NewProjectSchemaError =
  generateBlankError(schemaKeys.options);
